/* eslint-disable */

// Optional PostHog analytics module. The SDK is loaded dynamically only when
// POSTHOG_TOKEN is set; every exported call is a safe no-op otherwise.
//
// Autocapture masks all element text and attributes. Session replays mask
// inputs and only elements marked `data-ph-mask` (names, dates of birth);
// `data-ph-block` excludes an element from replays entirely. Patient/report
// identifiers in URLs are kept by design.

import { IS_POSTHOG_ENABLED, POSTHOG_API_HOST, POSTHOG_TOKEN } from '../config';

type PosthogClient = (typeof import('posthog-js'))['default'];

type PosthogReady = Promise<PosthogClient>;

let posthogPromise: PosthogReady | null = null;

// posthog-js initialization is asynchronous; the `loaded` callback is the SDK's
// own signal that capture/identify are safe to call. Chaining every call on it
// avoids the SDK's uninitialized guard dropping early events.
const getPosthog = (): PosthogReady | null => {
  if (!IS_POSTHOG_ENABLED) {
    return null;
  }

  if (!posthogPromise) {
    posthogPromise = import('posthog-js').then(
      (module) =>
        new Promise<PosthogClient>((resolve) => {
          module.default.init(POSTHOG_TOKEN, {
            api_host: POSTHOG_API_HOST,
            // Inject X-Posthog-Session-Id / X-Posthog-Distinct-Id into every
            // same-origin API request so backend captures link to this session.
            tracing_headers: [window.location.hostname],
            autocapture: true,
            mask_all_text: true,
            mask_all_element_attributes: true,
            capture_pageview: { path: true, search: true },
            capture_exceptions: false,
            disable_surveys: true,
            // The self-hosted instance redirects versioned /static/<version>/
            // assets to /login, which has no CORS header; load the recorder from
            // the legacy path.
            strict_script_versioning: false,
            session_recording: {
              maskAllInputs: true,
              maskTextSelector: '[data-ph-mask]',
              blockSelector: '[data-ph-block]',
            },
            loaded: (posthog) => resolve(posthog as PosthogClient),
          });
        }),
    );
  }

  return posthogPromise;
};

const trackPosthogEvent = (
  eventName: string,
  properties: Record<string, unknown>,
): void => {
  void getPosthog()?.then((posthog) => posthog.capture(eventName, properties));
};

const identifyPosthogUser = (userId: string, email: string): void => {
  void getPosthog()?.then((posthog) =>
    posthog.identify(userId, email ? { email } : undefined),
  );
};

// Warms up the SDK so $pageview tracking starts as early as possible; safe
// to call unconditionally.
const initPosthog = (): void => {
  getPosthog();
};

// Registers the organization as a PostHog group so org-level analytics work.
// Safe to call repeatedly; no-op while the SDK is disabled.
const setPosthogOrganization = (
  organizationID: string,
  properties: Record<string, unknown> = {},
): void => {
  void getPosthog()?.then((posthog) =>
    posthog.group('organization', organizationID, properties),
  );
};

// Runs the callback once the SDK is ready; returns a cancel function that
// also cancels a callback not yet dispatched. No-op while disabled.
const onPosthogReady = (
  callback: (posthog: PosthogClient) => void,
): (() => void) => {
  const posthog = getPosthog();

  if (!posthog) {
    return () => undefined;
  }

  let cancelled = false;

  posthog.then((client) => {
    if (!cancelled) {
      callback(client);
    }
  });

  return () => {
    cancelled = true;
  };
};

export {
  identifyPosthogUser,
  initPosthog,
  onPosthogReady,
  setPosthogOrganization,
  trackPosthogEvent,
};

/* eslint-enable */
